import {useMemo} from 'react';
import {useQuery} from '@apollo/client';
import {
  ContactCorrespondenceNeeds,
  ContactCorrespondenceNeedsVariables,
} from '../../schema-types/ContactCorrespondenceNeeds';
import validateUUID from '../../utils/validateUUID';
import {GET_CONTACT_CORRESPONDENCE_NEEDS} from './correspondenceNeedQueries';

/**
 * The current correspondence need of every contact on an account, keyed by
 * contact id (DBO-80). Kept as its own small query rather than widening the
 * shared `AccountContact` fragment, so the overview grid can look a value up
 * without every contact mock in the codebase learning the field. The update
 * mutation returns the same fragment, so Apollo refreshes the map from the
 * mutation result without a refetch.
 */
export const useContactCorrespondenceNeeds = (
  accountId: string | undefined,
  {enabled = true}: {enabled?: boolean} = {}
) => {
  const {data, loading, error, refetch} = useQuery<
    ContactCorrespondenceNeeds,
    ContactCorrespondenceNeedsVariables
  >(GET_CONTACT_CORRESPONDENCE_NEEDS, {
    variables: {accountId: validateUUID(accountId)!},
    skip: !accountId || !enabled,
  });
  const needs = useMemo(() => {
    const byContact: Record<string, string | null> = {};
    (data?.contacts ?? []).forEach((contact) => {
      byContact[contact.id] = contact.specialCareRequirement;
    });
    return byContact;
  }, [data]);
  return {needs, loading, error, refetch};
};
